// Graph + device cache for the insurance dash. Reads only: Household_Policies
// through figApi's select reader, and the vault files through the same
// Family Vault drive the CENTCOM doc loader resolves.
//
// The cache is what makes the ID card show with no signal at a traffic stop:
// the last policy rows go to localStorage and the last ID card images to the
// Cache API (localStorage data URLs where the Cache API is missing). Every read
// and write is wrapped — private browsing or a full disk just means no cache.

import { getItemsSelect } from '../api/figApi';
import { resolveDriveId } from '../centcom/vaultDocLoader';

const GRAPH = 'https://graph.microsoft.com/v1.0';
export const POLICY_LIST = 'Household_Policies';
export const POLICY_COLUMNS = [
  'Title', 'PolicyNumber', 'Carrier', 'LOB', 'Status', 'EffectiveDate', 'ExpirationDate', 'Premium', 'KeyLimits',
  'InsuredAddress', 'Insureds', 'VaultFolder', 'DecPagePath', 'FullPolicyPaths', 'IDCardImagePaths', 'AgentName', 'AgentPhone',
  'ClaimsPhone', 'OpenItems', 'SortOrder',
];
const ROWS_KEY  = 'dh_insurancePolicies';
const CACHE     = 'dh-insurance-v1';
const IMG_KEY   = path => `dh_insuranceImg:${path}`;
const cacheUrl  = path => `/__insurance-cache/${encodeURIComponent(path)}`;
const vaultPath = path => path.split('/').map(encodeURIComponent).join('/');

// ── Policy rows ───────────────────────────────────────────────────────────────
export async function loadPolicyRows(token) {
  const rows = await getItemsSelect(token, POLICY_LIST, POLICY_COLUMNS);
  try { localStorage.setItem(ROWS_KEY, JSON.stringify(rows)); } catch { /* not cached */ }
  return rows;
}

export function cachedPolicyRows() {
  try { return JSON.parse(localStorage.getItem(ROWS_KEY) || 'null'); } catch { return null; }
}

// ── ID card images ────────────────────────────────────────────────────────────
// Last-loaded copy, or null.
export async function cachedImage(path) {
  try {
    if (typeof caches !== 'undefined') {
      const hit = await (await caches.open(CACHE)).match(cacheUrl(path));
      if (hit) return await hit.blob();
    }
  } catch { /* fall through */ }
  try {
    const dataUrl = localStorage.getItem(IMG_KEY(path));
    if (dataUrl) return await (await fetch(dataUrl)).blob();
  } catch { /* no copy */ }
  return null;
}

async function keepImage(path, blob) {
  try {
    if (typeof caches !== 'undefined') {
      await (await caches.open(CACHE)).put(cacheUrl(path), new Response(blob, { headers: { 'Content-Type': blob.type || 'image/png' } }));
      return;
    }
  } catch { /* try localStorage */ }
  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
    localStorage.setItem(IMG_KEY(path), dataUrl);
  } catch { /* not cached */ }
}

// Fresh copy from the vault; the cache is refreshed on the way through.
export async function fetchImage(token, path) {
  const driveId = await resolveDriveId(token);
  const res = await fetch(`${GRAPH}/drives/${driveId}/root:/${vaultPath(path)}:/content`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`ID card not found (${res.status})`);
  const blob = await res.blob();
  await keepImage(path, blob);
  return blob;
}

// ── Documents ─────────────────────────────────────────────────────────────────
// The vault file's SharePoint view link (opens the PDF in the browser).
export async function vaultWebUrl(token, path) {
  const driveId = await resolveDriveId(token);
  const res = await fetch(`${GRAPH}/drives/${driveId}/root:/${vaultPath(path)}?$select=webUrl`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Document not found (${res.status})`);
  return (await res.json()).webUrl;
}
